import { InstantSearch, Configure, Index } from 'react-instantsearch-dom'
import { connectStateResults, connectAutoComplete } from 'react-instantsearch/connectors'
import { Component, h } from 'preact'
import algoliasearch, { Client, QueryParameters, MultiResponse } from 'algoliasearch'
import globals from '../../../utils/globals'
import { SEARCH_TOKEN_URL } from '../../../libs/config'
import getWithInMemoryCache from '../../../utils/getWithInMemoryCache'
import AlgoliaSearch from './AlgoliaSearch'
import AlgoliaSearchForControl from './AlgoliaSearchForControl'
import { Hit } from 'js/src/preact-components/algolia/components'
import { SearchBox } from './SearchBox'
import Icon from '../../assets'
import Button from '../../../control-components/ui-components/Button'
import Phrase from '../../../libs/translationsUtils/phrase'
import _ from 'underscore'
import { algoliaAnswer, insights, AnswerHit, AlgoliaAnswerCallback } from '../../../services/algoliaService'
import WithABTest, { Version } from '../../utils/ABTest'

const SearchWithAutoComplete = connectAutoComplete(connectStateResults(AlgoliaSearch))
const SearchWithAutoCompleteForControl = connectAutoComplete(connectStateResults(AlgoliaSearchForControl))

interface Props {
  version: Version
  isControl?: boolean
  isFederatedSearch?: boolean
  isFederatedSalesforceSearch?: boolean
  isFederatedSkilljarSearch?: boolean
  isFederatedFreshdeskSearch?: boolean
  isAlgoliaAnswersActive?: boolean
  isUserGuest?: boolean
  searchPlaceholder?: string
  callback?: (hit: Hit) => void
  exclude?: number[]
  submitIconType?: string
  category?: number
  isParentCategory?: boolean
  searchByContentType?: string[]
  displayFilters?: boolean
}

interface State {
  searchById: boolean
  searchByContentType?: string[]
  finalInput: string
  searchClient?: Client
  listIndexes: string[]
  isFiltersOverlay: boolean
  getAnswers?: (input: string, cb: AlgoliaAnswerCallback) => void
}

interface SearchQuery {
  q: string
  contentType?: string
}

function getAnswer (applicationId: string, apiKey: string, indexes: string[]) {
  const cache: Record<string, AnswerHit> = {}
  const debouncedGetAnswerReq = _.debounce(async (input: string, cb: AlgoliaAnswerCallback) => {
    try {
      const hit = await algoliaAnswer(applicationId, apiKey, indexes, input)
      cb(hit)
    } catch (err) {
      cb(null)
    }
  }, 400)
  return (input: string, cb: AlgoliaAnswerCallback) => {
    if (cache[input] !== undefined) {
      cb(cache[input])
      return
    }
    debouncedGetAnswerReq(input, (hit: AnswerHit) => {
      cache[input] = hit
      cb(hit)
    })
  }
}

class BasicSearch extends Component<Props, State> {
  communityId: string
  defaultIndexList: string[]

  constructor (props: Props) {
    super(props)

    this.communityId = globals('communityId')
    this.defaultIndexList = [`${this.communityId}`]
    if (props.isFederatedSearch) this.defaultIndexList.push(`${this.communityId}-zendesk`)
    if (props.isFederatedSalesforceSearch) this.defaultIndexList.push(`${this.communityId}-salesforce`)
    if (props.isFederatedSkilljarSearch) this.defaultIndexList.push(`${this.communityId}-skilljar`)
    if (props.isFederatedFreshdeskSearch) this.defaultIndexList.push(`${this.communityId}-freshdesk`)

    this.state = {
      searchById: false,
      searchByContentType: props.searchByContentType,
      finalInput: '',
      searchClient: undefined,
      listIndexes: [],
      isFiltersOverlay: false,
      getAnswers: undefined
    }
    this.updateSearch = this.updateSearch.bind(this)
    this.filtersOverlayHandler = this.filtersOverlayHandler.bind(this)
    this.revertFilters = this.revertFilters.bind(this)
  }

  updateSearch (searchTerm: SearchQuery, next: () => void) {
    this.setState({
      searchById: searchTerm.q.match(/^[0-9]+$/) !== null,
      finalInput: searchTerm.q,
      searchByContentType: searchTerm.contentType ? [searchTerm.contentType] : this.props.searchByContentType
    }, next)
  }

  async componentDidMount () {
    const data: {client_id: string, token: string, basicToken: string} = await getWithInMemoryCache(SEARCH_TOKEN_URL, {})
    const searchClient = algoliasearch(data.client_id, data.token)
    const basicClient = algoliasearch(data.client_id, data.basicToken)

    // do not make a search query when the input is empty
    const originalSearch = searchClient.search.bind(searchClient)
    searchClient.search = async function<T> (requests: Array<{ indexName: string, query: string, params: QueryParameters }>, _?: (err: Error, res: MultiResponse<T>) => void) {
      if (requests.some(request => request?.params?.query === '')) {
        return { results: [] }
      }
      return originalSearch(requests)
    }

    let indexes = this.defaultIndexList
    try {
      indexes = (await basicClient.listIndexes()).items
        .map((item: {name: string}) => item.name)
        .filter((i: string) => this.defaultIndexList.includes(i))
    } catch (e) {
      console.log('Listing indices failed, using defaultIndexList')
    }

    this.setState({
      listIndexes: indexes,
      searchClient: searchClient,
      getAnswers: getAnswer(data.client_id, data.token, indexes)
    })

    insights('init', {
      appId: data.client_id,
      apiKey: data.token
    })
  }

  resetFilters (e: Event) {
    e.stopPropagation()
    e.preventDefault()

    window.dispatchEvent(new CustomEvent('resetFilters'))
  }

  revertFilters (e: Event) {
    e.stopPropagation()
    e.preventDefault()

    window.dispatchEvent(new CustomEvent('revertFilters'))
    this.filtersOverlayHandler(e, false)
  }

  filtersOverlayHandler (e: Event, value: boolean) {
    e.stopPropagation()
    e.preventDefault()

    window.dispatchEvent(new CustomEvent('filter', {
      detail: {
        display: value
      }
    }))

    if (value) {
      this.setState({
        isFiltersOverlay: value
      })
    } else {
      /*
        Items that should be in the filters are added via Portal component
        having setTimeout here is making state change async and give time to filter component to unmount
      */
      window.setTimeout(() => {
        this.setState({
          isFiltersOverlay: !this.state.isFiltersOverlay
        })
      }, 0)
    }

    const bodyEl = document.querySelector('body')

    if (bodyEl) {
      if (bodyEl.style.overflow === 'hidden') {
        bodyEl.style.overflow = 'visible'
      } else {
        bodyEl.style.overflow = 'hidden'
      }
    }
  }

  render (props: Props, state: State) {
    if (!state.searchClient) {
      return (
        <SearchBox
          submitIconType={props.submitIconType ?? ''}
        />
      )
    }
    return (
      <div className='search-and-filter-wrapper'>
        <PureSearch
          {...props}
          searchClient={state.searchClient}
          updateSearch={this.updateSearch}
          searchById={state.searchById}
          searchByContentType={state.searchByContentType}
          finalInput={state.finalInput}
          categoryId={props.category}
          communityId={this.communityId}
          submitIconType={props.submitIconType ?? ''}
          listIndexes={state.listIndexes}
          getAnswers={state.getAnswers}
        />
        {
        props.displayFilters
          ? <button
              className='instant-search__filter'
              onClick={(e: Event) => this.filtersOverlayHandler(e, true)}
            >
            <Icon width={18} height={12} color='#6B707E' icon='filter' />
          </button>

          : null
        }
        {state.isFiltersOverlay
          ? <div className={`instant-search__results-wrapper is-hidden-L ${!state.isFiltersOverlay ? '' : ''}`}>
            <div
              className={`instant-search__results-wrapper-filter ${!state.isFiltersOverlay
              ? 'instant-search__results-wrapper-filter--hide'
              : ''}`}
            >
              <div className='instant-search__results-wrapper-filter-header'>
                <Icon icon='close' onClick={e => this.revertFilters(e)} />
                <h3
                  className='instant-search__results-wrapper-filter-header-heading'
                >
                  <Phrase p='advanced.search.filters' />
                </h3>
                <button
                  onClick={e => this.resetFilters(e)}
                  className='instant-search__results-wrapper-filter-header-btn'
                >
                  <Phrase m='Common' p='advanced.search.filter.clear.all' />
                </button>
              </div>
              {
            /*
            * Filters can be added via Portal component from real place of filters.
            * Since Search does not include them it does not care about which items needs to be display in which order,
            * all those decisions can be done on filter component
            * */
            }
              <div className='instant-search__results-wrapper-filters' />
              <div
                className='instant-search__results-wrapper-filter-show-more-wrapper'
              >
                <Button
                  cta
                  class='instant-search__results-wrapper-filter-show-more'
                  onClick={(e: Event) => this.filtersOverlayHandler(e, false)}
                >
                  <Phrase p='show.results' />
                </Button>
              </div>
            </div>
          </div>
          : null}
      </div>
    )
  }
}

interface PureProps extends Props {
  version: Version
  communityId: string
  searchById: boolean
  searchByContentType?: string[]
  finalInput: string
  searchClient: algoliasearch
  updateSearch: (value: SearchQuery, cb: () => void) => void
  categoryId?: number
  groupId?: number
  listIndexes: string[]
  getAnswers?: State['getAnswers']
}

const getFilters = (props: PureProps) => {
  if (props.categoryId) {
    if (props.isParentCategory) {
      return `category:${props.categoryId}`
    } else {
      return `forum:${props.categoryId}`
    }
  } else if (props.groupId) {
    return `forum:${props.groupId}`
  }

  if (props.searchByContentType) {
    return props.searchByContentType.map(value => {
      return `content_type:${value}`
    }).join(' OR ')
  }
  return undefined
}

const PureSearch = (props: PureProps) => <InstantSearch
  searchClient={props.searchClient}
  indexName={props.communityId}
                                         >
  {
    props.listIndexes.includes(props.communityId)
      ? (
        <Index indexName={props.communityId}>
          <Configure
            hitsPerPage={5}
            filters={getFilters(props)}
            clickAnalytics
            analyticsTags={props.isControl
              ? ['control']
              : ['destination', 'live'].concat(props.isAlgoliaAnswersActive ? [`answers_version_${props.version}`] : [])}
            restrictSearchableAttributes={props.searchById ? ['id', 'private_id'] : ['title', 'first_post', 'answer', 'reply', 'tags', 'forum_title', 'category_title']}
          />
        </Index>
        )
      : null
  }

  {
    props.isFederatedSalesforceSearch && props.listIndexes.includes(`${props.communityId}-salesforce`)
      ? (
        <Index indexName={`${props.communityId}-salesforce`}>
          <Configure
            hitsPerPage={3}
            filters={getFilters(props)}
            clickAnalytics
            analyticsTags={props.isControl ? ['control'] : ['destination', 'live']}
          />
        </Index>
        )
      : null
  }

  {
    props.isFederatedSkilljarSearch && props.listIndexes.includes(`${props.communityId}-skilljar`)
      ? (
        <Index indexName={`${props.communityId}-skilljar`}>
          <Configure
            hitsPerPage={3}
            clickAnalytics
          />
        </Index>
        )
      : null
  }

  {
    props.isFederatedFreshdeskSearch && props.listIndexes.includes(`${props.communityId}-freshdesk`)
      ? (
        <Index indexName={`${props.communityId}-freshdesk`}>
          <Configure
            hitsPerPage={3}
            clickAnalytics
          />
        </Index>
        )
      : null
  }

  {
    props.isFederatedSearch && props.listIndexes.includes(`${props.communityId}-zendesk`)
      ? (
        <Index indexName={`${props.communityId}-zendesk`}>
          <Configure
            hitsPerPage={3}
            clickAnalytics
          />
        </Index>
        )
      : null
  }

  {
    props.isControl
      ? (
        <SearchWithAutoCompleteForControl
          updateSearch={(value: SearchQuery, next: () => void) => props.updateSearch(value, next)}
          searchById={props.searchById}
          finalInput={props.finalInput}
          placeholder={props.searchPlaceholder}
          callback={props.callback}
          exclude={props.exclude}
          submitIconType={props.submitIconType ?? ''}
        />
        )
      : (
        <SearchWithAutoComplete
          updateSearch={(value: SearchQuery, next: () => void) => props.updateSearch(value, next)}
          callback={props.callback}
          getAnswers={props.getAnswers}
          indexName={props.communityId}
          isAlgoliaAnswersActive={props.isAlgoliaAnswersActive}
        />
        )
  }

</InstantSearch>

export { PureSearch, SearchQuery }
const Search = WithABTest(BasicSearch, 'AlgoliaAnswers')

export default Search
